import type { LinkSnapshot, TaskSnapshot } from '../stores/types';
import { resolveTaskName } from '../utils/utils';

export type PredecessorValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

export type PredecessorLinkEndpoint = Pick<LinkSnapshot, 'source' | 'target'> | {
  source: string | number;
  target: string | number;
};

function buildTaskById(tasks: TaskSnapshot[]): Map<string, TaskSnapshot> {
  return new Map(tasks.map((t) => [String(t.id), t]));
}

function collectParentTaskIds(tasks: TaskSnapshot[]): Set<string> {
  const parentIds = new Set<string>();
  for (const t of tasks) {
    if (t.parentTaskId != null && String(t.parentTaskId).length > 0) {
      parentIds.add(String(t.parentTaskId));
    }
  }
  return parentIds;
}

function isSummaryPredecessor(
  sourceTask: TaskSnapshot | undefined,
  parentIds: Set<string>,
): boolean {
  if (!sourceTask) return false;
  return Boolean(sourceTask.summary) || parentIds.has(String(sourceTask.id));
}

function taskLabel(task: TaskSnapshot | undefined, fallback: string): string {
  if (!task) return fallback;
  return String(task.srNo ?? '').trim() || resolveTaskName(task) || fallback;
}

/** True when `ancestorId` is the same as `taskId` or any parent in its chain. */
export function isAncestorOf(
  ancestorId: string,
  taskId: string,
  byId: Map<string, TaskSnapshot>,
): boolean {
  const target = String(ancestorId);
  let current: string | null = String(taskId);
  while (current) {
    if (current === target) return true;
    const parent = byId.get(current);
    current = parent?.parentTaskId ? String(parent.parentTaskId) : null;
  }
  return false;
}

/**
 * True when adding dependency edge source→target would close a cycle in the
 * existing link graph (predecessor → successor).
 */
export function wouldCreateDependencyCycle(
  sourceId: string,
  targetId: string,
  links: PredecessorLinkEndpoint[],
): boolean {
  const source = String(sourceId);
  const target = String(targetId);
  if (!source || !target || source === target) return true;

  const successors = new Map<string, string[]>();
  for (const link of links) {
    const from = String(link.source);
    const to = String(link.target);
    if (!from || !to || from === to) continue;
    const list = successors.get(from);
    if (list) list.push(to);
    else successors.set(from, [to]);
  }

  // If target can already reach source, source→target is a cycle.
  const stack = [target];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const cur = stack.pop()!;
    if (cur === source) return true;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const next = successors.get(cur);
    if (next) {
      for (const id of next) stack.push(id);
    }
  }
  return false;
}

/**
 * O(n + L) validity map for the predecessor picker (16k-safe).
 * Prefer this over calling validatePredecessorLink once per task.
 */
export function buildPredecessorValidityMap(
  tasks: TaskSnapshot[],
  targetTaskId: string,
  links: PredecessorLinkEndpoint[] = [],
): Map<string, PredecessorValidationResult> {
  const target = String(targetTaskId);
  const byId = buildTaskById(tasks);
  const parentIds = collectParentTaskIds(tasks);
  const targetTask = byId.get(target);
  const targetLabel = taskLabel(targetTask, target);

  const ancestors = new Set<string>();
  let walk: string | null = targetTask?.parentTaskId ? String(targetTask.parentTaskId) : null;
  while (walk) {
    ancestors.add(walk);
    const parent = byId.get(walk);
    walk = parent?.parentTaskId ? String(parent.parentTaskId) : null;
  }

  const childrenByParent = new Map<string, string[]>();
  for (const t of tasks) {
    if (t.parentTaskId == null) continue;
    const p = String(t.parentTaskId);
    const id = String(t.id);
    const list = childrenByParent.get(p);
    if (list) list.push(id);
    else childrenByParent.set(p, [id]);
  }

  const descendants = new Set<string>();
  const descStack = [...(childrenByParent.get(target) ?? [])];
  while (descStack.length > 0) {
    const id = descStack.pop()!;
    if (descendants.has(id)) continue;
    descendants.add(id);
    const kids = childrenByParent.get(id);
    if (kids) {
      for (const k of kids) descStack.push(k);
    }
  }

  const reachableFromTarget = new Set<string>();
  if (links.length > 0) {
    const successors = new Map<string, string[]>();
    for (const link of links) {
      const from = String(link.source);
      const to = String(link.target);
      if (!from || !to || from === to) continue;
      const list = successors.get(from);
      if (list) list.push(to);
      else successors.set(from, [to]);
    }
    const stack = [target];
    const visited = new Set<string>();
    while (stack.length > 0) {
      const cur = stack.pop()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      const next = successors.get(cur);
      if (!next) continue;
      for (const id of next) {
        reachableFromTarget.add(id);
        stack.push(id);
      }
    }
  }

  const map = new Map<string, PredecessorValidationResult>();
  for (const task of tasks) {
    const source = String(task.id);
    if (source === target) {
      map.set(source, { ok: false, reason: 'A task cannot be its own predecessor.' });
      continue;
    }
    const sourceLabel = taskLabel(task, source);

    if (ancestors.has(source)) {
      map.set(
        source,
        task.milestone
          ? {
              ok: false,
              reason: `Cannot link “${targetLabel}” to its milestone parent “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
            }
          : {
              ok: false,
              reason: `Cannot link “${targetLabel}” to its parent/ancestor “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
            },
      );
      continue;
    }

    if (descendants.has(source)) {
      map.set(source, {
        ok: false,
        reason: `Cannot link “${targetLabel}” to its child/descendant “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
      });
      continue;
    }

    if (isSummaryPredecessor(task, parentIds)) {
      map.set(source, {
        ok: false,
        reason: `“${sourceLabel}” is a summary task — only leaf tasks can be predecessors.`,
      });
      continue;
    }

    if (reachableFromTarget.has(source)) {
      map.set(source, {
        ok: false,
        reason:
          'That dependency would create a circular schedule loop. Choose a different predecessor.',
      });
      continue;
    }

    map.set(source, { ok: true });
  }
  return map;
}

/**
 * Block parent/ancestor or descendant as predecessor (creates schedule loops).
 * Block summary/parent rows as predecessors — DHTMLX parent/child scheduling
 * treats those as constrained and often throws circular-link errors.
 * When `links` is provided, also block dependency-graph cycles.
 * Leaf milestones of unrelated branches remain allowed.
 */
export function validatePredecessorLink(
  sourceId: string,
  targetId: string,
  tasks: TaskSnapshot[],
  links?: PredecessorLinkEndpoint[],
): PredecessorValidationResult {
  const source = String(sourceId);
  const target = String(targetId);
  if (!source || !target) {
    return { ok: false, reason: 'Predecessor task could not be resolved.' };
  }
  if (source === target) {
    return { ok: false, reason: 'A task cannot be its own predecessor.' };
  }

  const byId = buildTaskById(tasks);
  const parentIds = collectParentTaskIds(tasks);
  const sourceTask = byId.get(source);
  const targetTask = byId.get(target);
  const sourceLabel = taskLabel(sourceTask, source);
  const targetLabel = taskLabel(targetTask, target);

  // Predecessor is an ancestor of the successor (e.g. child → parent milestone).
  if (isAncestorOf(source, target, byId)) {
    if (sourceTask?.milestone) {
      return {
        ok: false,
        reason: `Cannot link “${targetLabel}” to its milestone parent “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
      };
    }
    return {
      ok: false,
      reason: `Cannot link “${targetLabel}” to its parent/ancestor “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
    };
  }

  // Predecessor is a descendant of the successor (parent → child).
  if (isAncestorOf(target, source, byId)) {
    return {
      ok: false,
      reason: `Cannot link “${targetLabel}” to its child/descendant “${sourceLabel}” as a predecessor — that creates a recursive schedule loop.`,
    };
  }

  // Summary / parent rows cannot be predecessors (engine circular-link with children).
  if (isSummaryPredecessor(sourceTask, parentIds)) {
    return {
      ok: false,
      reason: `“${sourceLabel}” is a summary task — only leaf tasks can be predecessors.`,
    };
  }

  if (links && wouldCreateDependencyCycle(source, target, links)) {
    return {
      ok: false,
      reason:
        'That dependency would create a circular schedule loop. Choose a different predecessor.',
    };
  }

  return { ok: true };
}

/** Validate every source→target pair; return first failure. */
export function validatePredecessorLinks(
  pairs: Array<{ sourceId: string; targetId: string }>,
  tasks: TaskSnapshot[],
  links?: PredecessorLinkEndpoint[],
): PredecessorValidationResult {
  const provisional: PredecessorLinkEndpoint[] = links ? [...links] : [];
  for (const { sourceId, targetId } of pairs) {
    const result = validatePredecessorLink(
      sourceId,
      targetId,
      tasks,
      links ? provisional : undefined,
    );
    if (!result.ok) return result;
    if (links) provisional.push({ source: sourceId, target: targetId });
  }
  return { ok: true };
}
